import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CircleMarker,
  MapContainer,
  useMap,
} from "react-leaflet";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass,
  MapPin,
} from "@phosphor-icons/react";

import {
  prospects as prospectsApi,
  stats as statsApi,
} from "@/api";
import CommercialScopeFilter from "@/components/CommercialScopeFilter";
import { useAuth } from "@/context/AuthContext";
import ActaLocalBasemap from "@/components/maps/ActaLocalBasemap";
import CommerceMapResultsPanel from "@/components/maps/CommerceMapResultsPanel";
import { formatApiError } from "@/lib/api";
import { normalizeMapSearch } from "@/lib/maps/mapSearch";

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function cityContacts(city) {
  return [
    ...safeArray(city?.clients),
    ...safeArray(city?.prospects),
  ];
}

function markerRadius(value) {
  return Math.max(
    9,
    Math.min(
      30,
      9 + Math.sqrt(Math.max(1, Number(value || 0))) * 3.4,
    ),
  );
}

function contactMatches(contact, cityName, query) {
  if (!query) return true;

  const searchable = normalizeMapSearch([
    cityName,
    contact?.firstName,
    contact?.lastName,
    contact?.company,
    contact?.ownerName,
  ].filter(Boolean).join(" "));

  return searchable.includes(query);
}

function contactTypeCounts(contacts) {
  return contacts.reduce(
    (counts, contact) => {
      if (contact?.lifecycleStage === "client") {
        counts.clients += 1;
      } else {
        counts.prospects += 1;
      }

      return counts;
    },
    {
      clients: 0,
      prospects: 0,
    },
  );
}

function markerFill(status) {
  return status === "client"
    ? "var(--do-success)"
    : "var(--do-accent)";
}

function MapViewportSync({ cities }) {
  const map = useMap();
  const signature = useMemo(
    () => cities
      .map((city) => `${city.key}:${city.lat}:${city.lng}`)
      .join("|"),
    [cities],
  );

  useEffect(() => {
    const points = cities
      .filter(
        (city) => Number.isFinite(Number(city.lat))
          && Number.isFinite(Number(city.lng)),
      )
      .map((city) => [Number(city.lat), Number(city.lng)]);

    window.requestAnimationFrame(() => {
      map.invalidateSize({ pan: false });

      if (points.length === 1) {
        map.setView(points[0], 8, { animate: false });
      } else if (points.length > 1) {
        map.fitBounds(points, {
          padding: [38, 38],
          maxZoom: 9,
          animate: false,
        });
      }
    });
  }, [map, signature, cities]);

  return null;
}

export default function MapPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedCityKey, setSelectedCityKey] = useState("");
  const [commercialFilter, setCommercialFilter] = useState("all");
  const [commercialUsers, setCommercialUsers] = useState([]);

  const canFilterCommercials = user?.role === "admin";

  const selectedOwnerId = useMemo(() => {
    if (!canFilterCommercials || commercialFilter === "all") {
      return "";
    }

    if (commercialFilter === "self") {
      return String(user?.id || "");
    }

    if (commercialFilter.startsWith("owner:")) {
      return commercialFilter.slice(6);
    }

    return "";
  }, [
    canFilterCommercials,
    commercialFilter,
    user?.id,
  ]);

  useEffect(() => {
    if (!canFilterCommercials) {
      setCommercialUsers([]);
      setCommercialFilter("all");
      return undefined;
    }

    let cancelled = false;

    prospectsApi
      .listCommercialUsers()
      .then((rows) => {
        if (!cancelled) {
          setCommercialUsers(
            safeArray(rows).filter(
              (entry) =>
                entry?.id
                && [
                  "admin",
                  "manager",
                  "user",
                ].includes(entry.role),
            ),
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCommercialUsers([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canFilterCommercials]);

  const commercialOptions = useMemo(() => {
    const currentUserId = String(user?.id || "");

    return [
      {
        key: "all",
        value: "all",
        label: "Tous les commerciaux",
      },
      ...(currentUserId
        ? [
            {
              key: "self",
              value: "self",
              label: "Moi — utilisateur en cours",
            },
          ]
        : []),
      ...commercialUsers
        .filter(
          (entry) =>
            entry?.id
            && String(entry.id) !== currentUserId,
        )
        .map((entry) => ({
          key: `owner-${entry.id}`,
          value: `owner:${entry.id}`,
          label: entry.name,
        })),
    ];
  }, [
    commercialUsers,
    user?.id,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");

    statsApi
      .mapData({
        signal: controller.signal,
        ownerUserId: selectedOwnerId,
      })
      .then((payload) => {
        setCities(safeArray(payload?.cities));
      })
      .catch((requestError) => {
        const cancelled = requestError?.code === "ERR_CANCELED"
          || requestError?.name === "CanceledError"
          || controller.signal.aborted;

        if (!cancelled) {
          setError(
            formatApiError(
              requestError?.response?.data?.detail
                || requestError?.message
                || "Impossible de charger la carte.",
            ),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [
    retryKey,
    selectedOwnerId,
  ]);

  const geoCities = useMemo(
    () => cities.filter((city) => (
      city?.hasCoords
      && Number.isFinite(Number(city.lat))
      && Number.isFinite(Number(city.lng))
      && cityContacts(city).length > 0
    )),
    [cities],
  );

  const normalizedSearch = useMemo(
    () => normalizeMapSearch(search),
    [search],
  );

  const visibleCities = useMemo(
    () => geoCities
      .map((city) => {
        const contacts = cityContacts(city).filter((contact) =>
          contactMatches(contact, city.city, normalizedSearch));

        return contacts.length
          ? { ...city, visibleContacts: contacts }
          : null;
      })
      .filter(Boolean),
    [geoCities, normalizedSearch],
  );

  const searchResults = useMemo(
    () => normalizedSearch
      ? visibleCities.flatMap((city) => city.visibleContacts)
      : [],
    [normalizedSearch, visibleCities],
  );

  const selectedCity = useMemo(
    () => geoCities.find((city) => city.key === selectedCityKey) || null,
    [geoCities, selectedCityKey],
  );

  const panel = normalizedSearch
    ? {
        title: "Résultats",
        subtitle: `${searchResults.length} contact${searchResults.length > 1 ? "s" : ""}`,
        contacts: searchResults,
      }
    : selectedCity
      ? {
          title: selectedCity.city,
          subtitle: `${cityContacts(selectedCity).length} contact${cityContacts(selectedCity).length > 1 ? "s" : ""}`,
          contacts: cityContacts(selectedCity),
        }
      : null;

  const center = visibleCities.length
    ? [Number(visibleCities[0].lat), Number(visibleCities[0].lng)]
    : geoCities.length
      ? [Number(geoCities[0].lat), Number(geoCities[0].lng)]
      : [46.5, 2.5];

  const closePanel = () => {
    if (normalizedSearch) setSearch("");
    setSelectedCityKey("");
  };

  return (
    <main
      className="acta-map-page w-full min-w-0 px-4 py-4 sm:px-5 lg:px-6 acta-standard-page acta-commerce-page-standard"
      data-testid="map-page"
    >
      <section className="acta-map-content flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
        <div className="shrink-0 border-b border-[var(--do-border)] p-3 sm:p-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <label className="relative min-w-0 flex-1">
              <MagnifyingGlass
              size={18}
              weight="bold"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
            />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher une ville, un nom, un prénom ou une société..."
                className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg)] pl-10 pr-4 text-sm text-[var(--do-ink)] outline-none transition placeholder:text-[var(--do-muted)] focus:border-[var(--do-accent)]"
                data-testid="commercial-map-search"
              />
            </label>

            {canFilterCommercials ? (
              <div
                className="min-w-0 max-w-[46%] flex-[0_1_46%] sm:max-w-[220px] sm:flex-[0_1_220px] lg:w-[220px] lg:max-w-none lg:flex-none"
                data-testid="commercial-map-owner-filter"
              >
                <CommercialScopeFilter
                  value={commercialFilter}
                  onChange={setCommercialFilter}
                  options={commercialOptions}
                  ariaLabel="Filtrer la carte par commercial"
                  testId="commercial-map-owner-user-filter"
                />
              </div>
            ) : null}
          </div>

          <div className="mt-2 flex items-center gap-4 text-[11px] font-semibold text-[var(--do-muted)]">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-full bg-[var(--do-success)]"
                aria-hidden="true"
              />
              Clients
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-full bg-[var(--do-accent)]"
                aria-hidden="true"
              />
              Prospects
            </span>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div
            className="acta-map-canvas-shell relative min-h-[420px] flex-1 overflow-hidden"
            data-testid="interactive-commercial-map"
          >
            {loading ? (
              <div className="flex h-full min-h-[420px] items-center justify-center text-[var(--do-muted)]">
                Chargement de la carte…
              </div>
            ) : error ? (
              <div className="flex h-full min-h-[420px] flex-col items-center justify-center px-6 text-center text-red-500">
                <span>{error}</span>
                <button
                  type="button"
                  onClick={() => setRetryKey((value) => value + 1)}
                  className="mt-3 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-2 text-sm font-semibold text-[var(--do-ink)]"
                >
                  Réessayer
                </button>
              </div>
            ) : geoCities.length === 0 ? (
              <div className="flex h-full min-h-[420px] flex-col items-center justify-center px-6 text-center text-[var(--do-muted)]">
                <MapPin size={42} weight="duotone" className="mb-3" />
                Aucun client ou prospect géolocalisable.
              </div>
            ) : normalizedSearch && visibleCities.length === 0 ? (
              <div className="flex h-full min-h-[420px] items-center justify-center px-6 text-center text-sm text-[var(--do-muted)]">
                Aucun contact ne correspond à cette recherche.
              </div>
            ) : (
              <MapContainer
                center={center}
                zoom={5}
                minZoom={4}
                maxZoom={11}
                scrollWheelZoom
                wheelPxPerZoomLevel={120}
                attributionControl={false}
                className="acta-local-map h-full min-h-[420px] w-full"
              >
                <MapViewportSync cities={visibleCities} />
                <ActaLocalBasemap />

                {visibleCities.map((city) => {
                  const counts = contactTypeCounts(
                    city.visibleContacts,
                  );
                  const radius = markerRadius(
                    city.visibleContacts.length,
                  );
                  const hasClients = counts.clients > 0;
                  const hasProspects = counts.prospects > 0;
                  const eventHandlers = {
                    click: () => setSelectedCityKey(city.key),
                  };

                  return (
                    <React.Fragment key={city.key}>
                      <CircleMarker
                        center={[Number(city.lat), Number(city.lng)]}
                        radius={radius}
                        eventHandlers={eventHandlers}
                        pathOptions={{
                          color: "var(--do-bg-solid)",
                          weight: 2,
                          fillColor: markerFill(
                            hasClients ? "client" : "prospect",
                          ),
                          fillOpacity:
                            selectedCityKey === city.key
                              ? 0.98
                              : 0.84,
                        }}
                      />

                      {hasClients && hasProspects ? (
                        <CircleMarker
                          center={[Number(city.lat), Number(city.lng)]}
                          radius={Math.max(5, radius * 0.56)}
                          eventHandlers={eventHandlers}
                          pathOptions={{
                            color: "var(--do-bg-solid)",
                            weight: 2,
                            fillColor: markerFill("prospect"),
                            fillOpacity:
                              selectedCityKey === city.key
                                ? 0.98
                                : 0.9,
                          }}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </MapContainer>
            )}
          </div>

          {panel ? (
            <div className="min-h-0 border-t border-[var(--do-border)] lg:border-l lg:border-t-0">
              <CommerceMapResultsPanel
                title={panel.title}
                subtitle={panel.subtitle}
                contacts={panel.contacts}
                onClose={closePanel}
                onOpenContact={(contactId) =>
                  navigate(`/app/commerce/prospects/${contactId}`)}
              />
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}
